import {ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {trackById} from 'app/core/helpers/trackby';
import {NotificationsNodes, NotificationsService} from 'app/core/services/notifications/notifications.service';
import moment from 'moment-mini';
import {cloneDeep} from 'app/core/helpers/object';
import {BehaviorSubject, Subscription} from 'rxjs';
import {currentUser_currentUser_notifications_pageInfo} from 'app/core/services/gql/currentUser';
import {debounce} from 'app/core/helpers/decorators';
import {switchMap, tap} from 'rxjs/operators';
import {AdType} from '../../../globalTypes';

@Component({
    selector: 'notifications',
    templateUrl: './notifications.component.html',
    styleUrls: ['./notifications.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationsComponent implements OnInit, OnDestroy {
    @Input() preview = false;
    @Input() previewContent = '';
    @ViewChild('notificationListElem', {static: false}) public notificationListElem: ElementRef;

    isNotificationVisible = false;
    notifications: NotificationsNodes[] = [];
    hasUnreadNotifications = false;
    loading: boolean;
    loadingPage = false;

    protected readonly Boolean = Boolean;
    trackById = trackById;
    subs: Subscription[] = [];

    notificationBackground$: BehaviorSubject<string> = new BehaviorSubject<string>('');
    notificationBanner$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    constructor(
        private notificationsService: NotificationsService,
        private cd: ChangeDetectorRef
    ) {}

    ngOnInit() {
        this.getNotifications();
    }

    pagination: {
        totalCount: number;
        pageInfo: currentUser_currentUser_notifications_pageInfo;
    };
    public totalCount = 0;
    public pageIndex = 1;
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    public pageSize = 20;

    getNotifications(): void {
        this.loading = true;
        this.subs.push(
            this.notificationsService.getNotificationsFromCurrentUser().subscribe(notifications => {
                this.hasUnreadNotifications = Boolean(notifications.nodes.length) && Boolean(notifications.unreadCount);
                this.notifications = notifications.nodes;

                this.pagination = {
                    totalCount: notifications.totalCount,
                    pageInfo: {...notifications.pageInfo},
                };
                this.pageIndex += 1;

                this.loading = false;
                this.cd.detectChanges();
            })
        );
    }

    openNotifications() {
        this.isNotificationVisible = !this.isNotificationVisible;
    }

    watchNotification(event: MouseEvent, node: NotificationsNodes) {
        event.preventDefault();
        this.loading = true;
        this.subs.push(
            this.notificationsService
                .watchNotification(node.id)
                .pipe(
                    switchMap(() =>
                        this.notificationsService.fetchNotifications(this.pageIndex, this.pageSize).pipe(
                            tap(e => {
                                this.hasUnreadNotifications = e.unreadCount > 0;
                            })
                        )
                    )
                )
                .subscribe(watch => {
                    if (watch) {
                        this.loading = false;
                        this.redrawNotifications(node);
                        this.cd.detectChanges();
                        window.open(node.url, '_blank');
                    }
                })
        );
    }

    redrawNotifications(node: NotificationsNodes) {
        const nodesCopy: NotificationsNodes[] = cloneDeep(this.notifications);
        for (let i = 0; i < nodesCopy.length; i++) {
            if (nodesCopy[i].id === node.id) {
                nodesCopy[i].isRead = true;
            }
        }

        this.notifications = [...nodesCopy];
    }

    formattingDate(date: string) {
        let currentDate = moment(new Date(+date * 1000));
        let passedDays = moment().diff(currentDate, 'days');
        return passedDays <= 30 ? currentDate.fromNow() : currentDate.format('DD.MM.YYYY');
    }

    ngOnDestroy() {
        this.subs.forEach(sub => sub.unsubscribe());
    }

    @debounce()
    onScrollLoadData() {
        const _this = this;
        const nativeElement = _this.notificationListElem.nativeElement;
        const nodesCopy: NotificationsNodes[] = cloneDeep(_this.notifications);

        if (
            nativeElement.clientHeight + Math.round(nativeElement.scrollTop) === nativeElement.scrollHeight &&
            _this.notifications.length !== _this.pagination.totalCount
        ) {
            _this.loadingPage = true;
            _this.cd.detectChanges();
            _this.notificationsService
                .fetchNotifications(_this.pageIndex, _this.pageSize)
                .pipe(
                    tap(notifications => {
                        notifications.nodes.forEach(node => {
                            if (_this.notifications.some(n => n.id === node.id)) {
                                return;
                            }

                            const idx = nodesCopy.findIndex(n => n.id === node.id);
                            if (idx === -1) {
                                nodesCopy.push(node);
                            }
                        });
                    })
                )
                .subscribe(res => {
                    _this.notifications = [...nodesCopy];
                    _this.loadingPage = false;
                    _this.cd.detectChanges();
                    _this.pageIndex += 1;
                });
        }
    }

    deleteNotification(id: string): void {
        this.loading = true;
        this.notificationsService.deleteNotification(id).subscribe(deleted => {
            if (deleted) {
                this.removeNotificationFromList(id);
                this.loading = false;
                this.cd.detectChanges();
            }
        });
    }

    removeNotificationFromList(id: string) {
        let list = cloneDeep(this.notifications);
        let index = list.findIndex(value => value.id === id);
        if (index > -1) {
            list.splice(index, 1);
        }
        this.notifications = [...list];
        this.hasUnreadNotifications = Boolean(this.notifications.some(n => !n.isRead));
    }

    protected readonly AdType = AdType;
}
